// MARK://dhk8/pos/hooks/use-cart-broadcast
// Sincroniza el carrito del operador hacia la pantalla de cliente en una segunda
// ventana del mismo navegador. POS de Barra de 2 pantallas (1 sola máquina):
//
//   ┌──────────────────────┐         BroadcastChannel('dhk8-pos-barra')
//   │ Pantalla del operador │ ───────────────────────────────────────────►
//   │  (/pos/barra)         │           "cart" envelope (snapshot)
//   └──────────────────────┘
//                                                                 ▼
//                                                  ┌──────────────────────┐
//                                                  │ Pantalla del cliente │
//                                                  │  (/barra-display)    │
//                                                  └──────────────────────┘
//
// Razones de diseño:
//   - BroadcastChannel es nativo del navegador, sin servidor, latencia ~ms.
//   - Mismo origen ⇒ funciona entre ventanas/pestañas del MISMO POS.
//   - Snapshot completo en cada cambio: la pantalla de cliente es stateless y
//     siempre refleja el último estado conocido — si abre tarde, pide HELLO y
//     el operador responde con la imagen actual.
//   - Si el navegador no soporta BroadcastChannel (Safari viejo) hay un fallback
//     vía `localStorage` + evento `storage` (también dispara entre ventanas del
//     mismo origen). Mismo formato de envelope.
//
// El protocolo es deliberadamente pequeño:
//   { type: 'cart',  payload: CartSnapshot }   ← operador → cliente
//   { type: 'hello' }                          ← cliente → operador (al montar)
//   { type: 'bye'  }                           ← operador → cliente (cerrar)

import { useEffect, useRef } from 'react'
import type { CartLine } from './use-cart.js'

/**
 * S1-5 hardening 2026-05-24 — el canal antes era literal `dhk8-pos-barra`,
 * compartido entre TODOS los tenants/terminales del mismo origen. Si un
 * navegador abría 2 sucursales en pestañas distintas (multi-tenant en el
 * mismo browser, p.ej. SuperAdmin), el cart de A se mostraba en el
 * Customer Display de B (cross-tenant leak en el cliente). Ahora cada
 * canal se suffija con el tenantId activo (`dhk8-pos-barra:<tenantId>`).
 *
 * El constante legacy `CART_CHANNEL` queda exportado por compat con
 * tests/consumers; producción debe usar `cartChannelName(tenantId)`.
 */
export const CART_CHANNEL = 'dhk8-pos-barra'
const LS_KEY_BASE = 'dhk8-pos-barra:cart'

/** Suffija el canal/LS key con el tenantId activo. Sin tenantId vuelve
 *  al nombre legacy (back-compat con preview-stub / dev). */
export function cartChannelName(tenantId: string | null | undefined): string {
  return tenantId && tenantId.length > 0 ? `${CART_CHANNEL}:${tenantId}` : CART_CHANNEL
}
function lsKeyFor(tenantId: string | null | undefined): string {
  return tenantId && tenantId.length > 0 ? `${LS_KEY_BASE}:${tenantId}` : LS_KEY_BASE
}

export interface BroadcastPromo {
  label: string
  amountCents: number // descuento aplicado, en centavos (positivo)
}

export interface CartSnapshot {
  /** Versión del esquema; bump si se rompe compat. */
  v: 1
  /** Timestamp emisor (ms epoch) — para mostrar "última actualización" / debug. */
  ts: number
  /** Marca del local (display name + logoUrl). El cliente lo prefiere antes de fetch. */
  branding?: { name?: string; logoUrl?: string | null }
  lines: CartLine[]
  subtotalCents: number
  /** Promos aplicadas (si las hay). Vacío ⇒ no hay nada que mostrar. */
  promos: BroadcastPromo[]
  totalCents: number
  /** Si el operador acaba de cerrar una venta, mandamos el folio para confirmar al cliente. */
  lastSale?: { folio: string; totalCents: number } | null
}

type Envelope =
  | { type: 'cart'; payload: CartSnapshot }
  | { type: 'hello' }
  | { type: 'bye' }

// ── Sender (operator screen) ────────────────────────────────────────────────

interface SenderHandle {
  send: (snapshot: CartSnapshot) => void
  close: () => void
}

function openSender(tenantId: string | null | undefined): SenderHandle {
  let last: CartSnapshot | null = null
  const channelName = cartChannelName(tenantId)
  const lsKey = lsKeyFor(tenantId)
  const hasBC = typeof BroadcastChannel !== 'undefined'
  const channel = hasBC ? new BroadcastChannel(channelName) : null

  const post = (env: Envelope): void => {
    if (channel) {
      try { channel.postMessage(env) } catch { /* ignore */ }
    }
    // Fallback `storage` (siempre encendido — barato y robusto). Cambiar el valor
    // (no la clave) en localStorage dispara `storage` SOLO en otras ventanas del
    // mismo origen, lo cual es exactamente lo que queremos.
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem(lsKey, JSON.stringify({ ...env, _bump: Date.now() })) }
      catch { /* quota / disabled */ }
    }
  }

  // Si la pantalla de cliente arranca DESPUÉS, manda HELLO y el operador re-envía
  // el último snapshot.
  const onHello = (env: Envelope): void => {
    if (env.type === 'hello' && last) post({ type: 'cart', payload: last })
  }
  if (channel) {
    channel.onmessage = (ev: MessageEvent<Envelope>) => onHello(ev.data)
  }
  const onStorage = (ev: StorageEvent): void => {
    if (ev.key !== lsKey || !ev.newValue) return
    try {
      const env = JSON.parse(ev.newValue) as Envelope
      onHello(env)
    } catch { /* ignore */ }
  }
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage)

  return {
    send(snapshot) { last = snapshot; post({ type: 'cart', payload: snapshot }) },
    close() {
      post({ type: 'bye' })
      if (channel) channel.close()
      if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
    },
  }
}

/**
 * Hook para la pantalla DEL OPERADOR. Recibe un snapshot del carrito en cada
 * render; cuando cambian lines/subtotal/total/lastSale, broadcastea. El cliente
 * (segunda ventana) consume eso.
 *
 * `enabled=false` → no abre canal ni broadcastea (estado por defecto: solo
 * empezamos cuando el operador presiona "Abrir pantalla de cliente").
 */
/**
 * S1-5 hardening 2026-05-24: agrega tenantId opcional para scopar el canal.
 * Sin tenantId el canal cae al nombre legacy `dhk8-pos-barra` (compat con
 * preview-stub/dev). En prod siempre se debe pasar el tenantId activo
 * para evitar cross-talk multi-tenant en el mismo browser.
 */
export function useBroadcastCart(snapshot: CartSnapshot, enabled: boolean, tenantId?: string | null): void {
  const handle = useRef<SenderHandle | null>(null)
  // Abrir / cerrar canal según `enabled`. Cambios en tenantId obligan a
  // reabrir (otro canal, otro LS key).
  useEffect(() => {
    if (!enabled) {
      if (handle.current) { handle.current.close(); handle.current = null }
      return
    }
    // Cerrar y reabrir si el tenant cambió mientras teníamos canal abierto.
    if (handle.current) { handle.current.close(); handle.current = null }
    handle.current = openSender(tenantId ?? null)
    // Manda el estado actual al instante (la 2ª ventana puede haber abierto antes).
    handle.current.send(snapshot)
    return () => {
      // Cleanup solo cuando se DESHABILITA explícitamente o se desmonta la pág.
      // No cerramos en cada render: el efecto que sigue se encarga de mandar.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tenantId])

  // Broadcast en cada cambio relevante mientras esté habilitado.
  useEffect(() => {
    if (!enabled || !handle.current) return
    handle.current.send(snapshot)
  }, [enabled, snapshot])

  // Cerrar canal al desmontar la página del operador.
  useEffect(() => {
    return () => {
      if (handle.current) { handle.current.close(); handle.current = null }
    }
  }, [])
}

// ── Receiver (customer-facing screen) ───────────────────────────────────────

/**
 * Suscriptor para la pantalla DE CLIENTE (/barra-display). Llama al callback
 * cada vez que llega un snapshot del operador. Al montar, manda HELLO para que
 * el operador re-envíe su estado actual (caso: la 2ª ventana abre DESPUÉS).
 */
/**
 * S1-5 hardening 2026-05-24: igual que useBroadcastCart, acepta tenantId
 * para scopar el canal. Sin tenantId cae al canal legacy `dhk8-pos-barra`
 * (compat). Lane 7 (CustomerDisplay*) usará esta firma post-S1-5.
 */
export function subscribeCart(
  onSnapshot: (s: CartSnapshot) => void,
  onBye?: () => void,
  tenantId?: string | null,
): () => void {
  const channelName = cartChannelName(tenantId ?? null)
  const lsKey = lsKeyFor(tenantId ?? null)
  const hasBC = typeof BroadcastChannel !== 'undefined'
  const channel = hasBC ? new BroadcastChannel(channelName) : null

  const handle = (env: Envelope): void => {
    if (env.type === 'cart') onSnapshot(env.payload)
    else if (env.type === 'bye') onBye?.()
  }
  if (channel) {
    channel.onmessage = (ev: MessageEvent<Envelope>) => handle(ev.data)
    try { channel.postMessage({ type: 'hello' } satisfies Envelope) } catch { /* ignore */ }
  }
  const onStorage = (ev: StorageEvent): void => {
    if (ev.key !== lsKey || !ev.newValue) return
    try { handle(JSON.parse(ev.newValue) as Envelope) } catch { /* ignore */ }
  }
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage)

  // Fallback inicial: si BroadcastChannel no existe, leer el último valor de LS.
  if (!channel && typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(lsKey)
      if (raw) handle(JSON.parse(raw) as Envelope)
    } catch { /* ignore */ }
  }
  // Y siempre intentar HELLO vía LS también (por si el operador solo escucha LS).
  if (typeof localStorage !== 'undefined') {
    try { localStorage.setItem(lsKey, JSON.stringify({ type: 'hello', _bump: Date.now() })) }
    catch { /* ignore */ }
  }

  return () => {
    if (channel) channel.close()
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
  }
}
